import React, { useState, useCallback, useEffect } from "react";
import { Button } from "@amzn/meridian";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setScannedBarcode,
  addScannedBarcode,
  setBarcodeScanningMode,
} from "src/store/inspectionCaptureSlice";
import BarcodeScanner from "./BarcodeScanner";
import DuplicateBarcodeAlertModal from "./DuplicateBarcodeAlertModal";
import UnkownObjectBarcodeAlertModal from "./UnkownObjectBarcodeAlertModal";
import ManualBarcodeEntryModal from "./ManualBarcodeEntryModal";
import "./BarcodeFrame.scss";

interface BarcodeFrameProps {
  idealHeight?: number;
  idealWidth?: number;
  onBarcodeConfirmed?: () => void;
}

const BarcodeFrame: React.FC<BarcodeFrameProps> = ({
  idealHeight = 1080,
  idealWidth = 1920,
  onBarcodeConfirmed,
}) => {
  const dispatch = useAppDispatch();

  const { scannedBarcode, scannedBarcodes } = useAppSelector(
    (state) => state.inspectionCapture
  );

  const [isScanning, setIsScanning] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showUnknownObjectBarcodeAlert, setShowUnknownObjectBarcodeAlert] = useState(false);
  const [duplicateBarcode, setDuplicateBarcode] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Single source of truth for barcode validation — used by both camera scan and manual entry
  const validateAndProcessBarcode = (barcodeValue: string) => {
    dispatch(setScannedBarcode(barcodeValue));

    if (scannedBarcodes.includes(barcodeValue)) {
      setDuplicateBarcode(barcodeValue);
      setShowDuplicateAlert(true);
      return;
    }

    const barcodePrefix = barcodeValue.substring(0, 3).toUpperCase();
    if (barcodePrefix !== "HAZ" && barcodePrefix !== "NHZ") {
      setShowUnknownObjectBarcodeAlert(true);
      return;
    }

    handleConfirm(barcodeValue);
  };

  const handleScan = useCallback(
    (result: any) => {
      if (result && result.length > 0) {
        const barcodeValue = result[0].rawValue;

        // Filter out bogus barcodes (pure numeric values) — keep camera running
        if (/^\d+$/.test(barcodeValue)) {
          return;
        }

        validateAndProcessBarcode(barcodeValue);
      }
    },
    []
  );

  const handleError = useCallback((error: any) => {
    console.error("Camera error:", error);
  }, []);

  const handleConfirm = (barcodeValue: string) => {
    // Add barcode to scanned list
    if (barcodeValue) {
      dispatch(addScannedBarcode(barcodeValue));
    }
    // If callback provided (workflow mode), call it
    if (onBarcodeConfirmed) {
      onBarcodeConfirmed();
    } else {
      // Otherwise, exit barcode scanning mode (inspection mode)
      dispatch(setBarcodeScanningMode(false));
    }
  };

  const handleRescan = () => {
    dispatch(setScannedBarcode(null));
    setShowUnknownObjectBarcodeAlert(false);
  };

  const handleDuplicateAlertClose = () => {
    setShowDuplicateAlert(false);
    setDuplicateBarcode("");
  };

  const handleManualBarcodeSubmit = (barcode: string) => {
    setShowManualEntry(false);
    validateAndProcessBarcode(barcode);
  };

  useEffect(() => {
    setIsScanning(true);
  }, []);

  return (
    <>
      {/* Camera View - Full Page */}
      <BarcodeScanner
        isScanning={isScanning}
        onScan={handleScan}
        onError={handleError}
        idealHeight={idealHeight}
        idealWidth={idealWidth}
      />

      {/* Barcode Confirmation Modal */}
      <UnkownObjectBarcodeAlertModal
        open={showUnknownObjectBarcodeAlert}
        barcode={scannedBarcode || ""}
        onRescan={handleRescan}
      />

      {/* Duplicate Barcode Alert Modal */}
      <DuplicateBarcodeAlertModal
        open={showDuplicateAlert}
        barcode={duplicateBarcode}
        onClose={handleDuplicateAlertClose}
      />

      {/* Manual Barcode Entry Button */}
      <div className="manual-entry-button-container">
        <Button
          type="primary"
          onClick={() => {
            setShowManualEntry(true);
          }}
        >
         Click to Enter Barcode
        </Button>
      </div>

      {/* Manual Barcode Entry Modal */}
      <ManualBarcodeEntryModal
        open={showManualEntry}
        onSubmit={handleManualBarcodeSubmit}
        onClose={() => {
          setShowManualEntry(false);
        }}
      />
    </>
  );
};

export default BarcodeFrame;
